import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ScanLine, Loader2, X, ShoppingBag, ArrowDownWideNarrow, ArrowUpNarrowWide, PackageSearch } from "lucide-react";
import { apiError } from "@/lib/api";
import { NUM } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BarcodeScanner from "@/components/BarcodeScanner";
import { fetchHistory, supKey, withTrend, supplierStats, periodParams } from "./purchaseApi";
import { KpiStrip, SupplierTable, HistoryTable, ScanNotice, useDocOpen, PeriodFilter } from "./HistoryParts";

const chip = (on) => cn("inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors", on ? "bg-foreground text-background shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/70");

function ProductChips({ products, value, onChange }) {
  if (products.length < 2) return null;
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="ph-products">
      <span className="text-[11px] font-semibold text-muted-foreground shrink-0">Eşleşen ürünler:</span>
      <button type="button" className={chip(!value)} onClick={() => onChange("")} data-testid="ph-product-all">Tümü ({NUM(products.reduce((s, p) => s + p.count, 0))})</button>
      {products.map((p, i) => (
        <button key={p.key} type="button" className={chip(value === p.key)} onClick={() => onChange(value === p.key ? "" : p.key)} title={p.name} data-testid={`ph-product-${i}`}>
          <span className="truncate max-w-[220px]">{p.name}</span>{p.code && <span className="font-mono opacity-70">{p.code}</span>}<span className="tnum opacity-70">· {NUM(p.count)}</span>
        </button>
      ))}
    </div>
  );
}

function Filters({ src, setSrc, sup, setSup, stats, asc, setAsc }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={src} onValueChange={setSrc}>
        <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="ph-filter-source"><SelectValue /></SelectTrigger>
        <SelectContent>{[["all", "Tüm kaynaklar"], ["efatura", "Gelen e-Fatura"], ["manuel", "Elle girilen"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={sup} onValueChange={setSup}>
        <SelectTrigger className="h-9 w-[240px] text-sm" data-testid="ph-filter-supplier"><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-[320px]"><SelectItem value="all">Tüm tedarikçiler ({NUM(stats.length)})</SelectItem>{stats.map((s) => <SelectItem key={s.key} value={s.key}>{s.supplier}</SelectItem>)}</SelectContent>
      </Select>
      <Button variant="outline" size="sm" className="h-9" onClick={() => setAsc(!asc)} data-testid="ph-sort-toggle">
        {asc ? <ArrowUpNarrowWide className="h-4 w-4 mr-1.5" /> : <ArrowDownWideNarrow className="h-4 w-4 mr-1.5" />}{asc ? "Eskiden yeniye" : "Yeniden eskiye"}
      </Button>
    </div>
  );
}

/** Stok Yönetimi › Alış Geçmişi: ürün adı / kodu / barkod → aldığınız faturalar ve tedarikçiler, en son alıştan en eskiye. */
export default function PurchaseHistoryTab() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pk, setPk] = useState("");
  const [sup, setSup] = useState("all");
  const [src, setSrc] = useState("all");
  const [asc, setAsc] = useState(false);
  const [scan, setScan] = useState(false);
  const [per, setPer] = useState({ period: "all", from: "", to: "" });
  const seq = useRef(0);
  const doc = useDocOpen();

  const run = async (text) => {
    const s = ++seq.current;
    if (!text.trim()) { setRes(null); setLoading(false); return; }
    setLoading(true);
    try {
      const d = await fetchHistory({ q: text.trim(), limit: 1500, ...periodParams(per) });
      if (s === seq.current) { setRes(d); setPk(""); setSup("all"); }
    } catch (e) { if (s === seq.current) toast.error(apiError(e)); }
    finally { if (s === seq.current) setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(() => run(q), 300); return () => clearTimeout(t); }, [q, per]); // eslint-disable-line react-hooks/exhaustive-deps

  const base = useMemo(() => (res?.rows || []).filter((r) => (!pk || r.pkey === pk) && (src === "all" || r.source === src)), [res, pk, src]);
  const stats = useMemo(() => supplierStats(base), [base]);
  const rows = useMemo(() => {
    const f = withTrend(sup === "all" ? base : base.filter((r) => supKey(r) === sup));
    return asc ? [...f].reverse() : f;
  }, [base, sup, asc]);

  return (
    <div className="space-y-4" data-testid="purchase-history-tab">
      <div className="bg-card border rounded-xl p-4 space-y-3 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0"><ShoppingBag className="h-5 w-5" /></div>
          <div className="min-w-0">
            <h2 className="font-head font-black text-lg leading-tight">Alış Geçmişi</h2>
            <p className="text-xs text-muted-foreground">Ürün adı, kodu veya barkodunu yazın — ürünü aldığınız tüm faturalar ve tedarikçiler en son alıştan en eskiye listelenir (gelen e-faturalar — kabul edilmemiş olanlar dahil — + elle girilen alış faturaları).</p>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") setQ(""); }} placeholder="Ürün adı, kodu veya barkod yazın…" className="h-11 pl-9 pr-9 rounded-xl text-[14px]" data-testid="ph-search-input" />
            {loading ? <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
              : q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:bg-muted" aria-label="Temizle" data-testid="ph-search-clear"><X className="h-3.5 w-3.5" /></button>}
          </div>
          <Button type="button" variant="secondary" className="h-11 rounded-xl px-3.5 text-white" onClick={() => setScan(true)} data-testid="ph-scan-btn"><ScanLine className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Barkod</span></Button>
        </div>
        <PeriodFilter value={per} onChange={setPer} />
        {res && <ProductChips products={res.products} value={pk} onChange={setPk} />}
        {res && res.rows.length > 0 && <Filters src={src} setSrc={setSrc} sup={sup} setSup={setSup} stats={stats} asc={asc} setAsc={setAsc} />}
        <ScanNotice unscanned={res?.unscanned} onDone={() => run(q)} />
      </div>

      {!q.trim() && (
        <div className="rounded-xl border border-dashed bg-card/50 py-14 text-center text-sm text-muted-foreground" data-testid="ph-empty">
          <PackageSearch className="h-10 w-10 mx-auto mb-2 text-muted-foreground/40" />Aramak istediğiniz ürünün adını veya kodunu yazın.
        </div>
      )}
      {q.trim() && res && !loading && res.rows.length === 0 && <div className="rounded-xl border bg-card py-10 text-center text-sm text-muted-foreground" data-testid="ph-no-result">"{q}" için {per.period === "all" ? "" : "seçili dönemde "}alış faturası kalemi bulunamadı.</div>}
      {res && res.rows.length > 0 && rows.length === 0 && <div className="rounded-xl border bg-card py-8 text-center text-sm text-muted-foreground" data-testid="ph-filter-empty">Seçili süzgeçlere uyan alış kalemi yok — kaynak / tedarikçi / ürün süzgecini değiştirin.</div>}
      {rows.length > 0 && (
        <>
          <KpiStrip rows={rows} />
          <SupplierTable stats={stats} active={sup} onPick={(k) => setSup(sup === k ? "all" : k)} />
          <HistoryTable rows={rows} doc={doc} />
        </>
      )}
      {res && res.total > res.rows.length && <p className="text-xs text-muted-foreground text-center" data-testid="ph-truncated">Toplam {NUM(res.total)} kalemden en yeni {NUM(res.rows.length)} kalem gösteriliyor — aramayı daraltın.</p>}
      {doc.dialog}
      <BarcodeScanner open={scan} onClose={() => setScan(false)} onDetected={(c) => { setScan(false); setQ(c); }} />
    </div>
  );
}
